import { applyAuWording, metricText } from '../strings/au.js';
import { setUnitSystem, isMetric } from './units.js';
import { syncInputUnits } from './unit-inputs.js';

// The Australian site is the same build served under /au/, so the URL decides
// which site a visitor is on. A signed-in account's own market still wins.
export const SITE_MARKET = window.location.pathname.startsWith('/au/') ? 'AU' : 'US';

// Android app: it always loads the US pages, so the phone's region setting
// stands in for the site (English (Australia) -> AU, anything else -> US).
// Decides a signed-out phone's experience and a new account's market.
const DEVICE_MARKET = window.Capacitor?.isNativePlatform?.()
    ? ((navigator.language || '').split('-')[1] || '').toUpperCase() === 'AU' ? 'AU' : 'US'
    : null;

export function pagePath(path) {
    return SITE_MARKET === 'AU' ? `/au${path}` : path;
}

// Which market a NEW account belongs to. The backend reads this only when it
// creates the account. Website: the site the person started signing in from
// (Cognito always returns to /app.html, so it's remembered across the trip).
// Android app: the phone's region setting.
const SIGNUP_KEY = 'zephyr_signup_market';
const SIGNUP_HINT_MS = 2 * 60 * 60 * 1000;

export function rememberSignupSite() {
    try { localStorage.setItem(SIGNUP_KEY, JSON.stringify({ market: SITE_MARKET, at: Date.now() })); } catch (e) { /* storage blocked */ }
}

export function signupMarketHint() {
    if (DEVICE_MARKET) return DEVICE_MARKET;
    try {
        const hint = JSON.parse(localStorage.getItem(SIGNUP_KEY) || 'null');
        if (hint && Date.now() - hint.at < SIGNUP_HINT_MS && (hint.market === 'AU' || hint.market === 'US')) return hint.market;
    } catch (e) { /* storage blocked */ }
    return SITE_MARKET;
}

// The signed-in account's market ('US' | 'AU') and units, remembered on this
// device so a page shows the right experience before the profile has loaded.
// Cleared on sign-out. Signed out, the site's market applies.
const ACCOUNT_KEY = 'zephyr_account_market';
let account = null; // { market, units }
try {
    const saved = JSON.parse(localStorage.getItem(ACCOUNT_KEY) || 'null');
    if (saved && (saved.market === 'US' || saved.market === 'AU')) account = saved;
} catch (e) { /* storage blocked */ }
let profileApplied = false;

export function currentMarket() {
    return account?.market || DEVICE_MARKET || SITE_MARKET;
}

// The unit system a page should start in before any profile loads.
export function defaultUnits() {
    return account?.units || (currentMarket() === 'AU' ? 'metric' : 'imperial');
}

// Applies a loaded profile: its market, then its units (an account without
// its own units setting gets its market's default).
export function applyAccount(profile) {
    profileApplied = true;
    setAccount(profile?.market, profile?.units);
}

// A new account's market from route-status, used only when no profile has
// loaded yet (the profile doesn't exist until route-status creates it).
export function applyAccountMarketHint(market) {
    if (!profileApplied && (market === 'US' || market === 'AU')) setAccount(market, undefined);
}

function setAccount(rawMarket, rawUnits) {
    const before = currentMarket();
    const market = rawMarket === 'AU' ? 'AU' : 'US';
    const units = rawUnits === 'metric' || rawUnits === 'imperial' ? rawUnits : (market === 'AU' ? 'metric' : 'imperial');
    const changed = account && (account.market !== market || account.units !== units);
    account = { market, units };
    try { localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account)); } catch (e) { /* storage blocked */ }
    // Page text already rewritten for the old settings can't be put back.
    if (changed && localisedPage) { window.location.reload(); return; }
    setUnitSystem(units);
    syncInputUnits();
    applyMarketToPage();
    if (market !== before) window.dispatchEvent(new CustomEvent('zephyr:marketchange', { detail: { market } }));
}

export function clearAccountMarket() {
    account = null;
    try { localStorage.removeItem(ACCOUNT_KEY); } catch (e) { /* storage blocked */ }
}

// Locale for dates and times: 22/10/2026 and 3:27 pm for Australian accounts.
export function displayLocale() {
    return currentMarket() === 'AU' ? 'en-AU' : 'en-US';
}

// Where the map opens before there's a route: the market's country.
const MAP_START = {
    US: { center: [-98.5795, 39.8283], zoom: 3.5 },
    AU: { center: [134.5, -27.5], zoom: 3.3 }
};
export function mapStartView() {
    return MAP_START[currentMarket()];
}

// Features built on US-only data (road quality, wind hotspots, states
// visited) are left out for Australian accounts and the /au/ site
// rather than shown empty. Check at the point of use: the account's market
// can arrive after the page has started.
export function hasUsOnlyData() {
    return currentMarket() !== 'AU';
}

// Wording for user-facing text. Pass plain text, not HTML markup.
export function w(text) {
    return currentMarket() === 'AU' ? applyAuWording(text) : text;
}

// The visitor's chosen site, read by the CloudFront redirect
// (cloudfront-functions/zephyr-append-html.js): 'au' always goes to /au/,
// 'us' is never redirected there.
export function chooseSite(market) {
    document.cookie = `zephyr_site=${market === 'AU' ? 'au' : 'us'}; path=/; max-age=31536000; samesite=lax; secure`;
}

// "Switch site" link (class "site-switch", hidden in the page). On since the
// Australian launch (2026-10-24). The CloudFront redirect stays off: it needs
// a custom origin request policy, which the CloudFront Free plan doesn't allow.
const SITE_SWITCH_ENABLED = true;

function setUpSiteSwitch() {
    if (!SITE_SWITCH_ENABLED) return;
    const other = SITE_MARKET === 'AU' ? 'US' : 'AU';
    const path = window.location.pathname.replace(/^\/au\//, '/');
    const href = (other === 'AU' ? '/au' + path : path) + window.location.search;
    document.querySelectorAll('.site-switch').forEach((el) => {
        el.textContent = other === 'AU' ? '🇦🇺 Australian site' : '🇺🇸 US site';
        el.setAttribute('href', href);
        el.style.display = '';
        el.addEventListener('click', () => chooseSite(other));
    });
}

// Pages the AU site leaves in US wording (scripts/build-au.mjs).
const NO_REWORDING = /\/(privacy|terms|tips)\.html$/;
let localisedPage = false;

// Makes the page match the account's market where it differs from the site's:
// a US account on an /au/ page goes to the same page on the US site, and an
// AU account on a US page (always the case in the Android app) gets the same
// wording and metric page text that scripts/build-au.mjs gives the /au/ pages.
export function applyMarketToPage() {
    if (account?.market === 'US' && SITE_MARKET === 'AU') {
        // Remembered so the Australian-visitor redirect never sends them back.
        chooseSite('US');
        window.location.replace(window.location.pathname.replace(/^\/au\//, '/') + window.location.search + window.location.hash);
        return;
    }
    if (currentMarket() !== 'AU') return;
    // US-only features (class "us-only") are hidden on the AU site too, and
    // Australian-only ones (class "au-only", hidden in the page) are shown.
    document.querySelectorAll('.us-only').forEach((el) => { el.style.display = 'none'; });
    document.querySelectorAll('.au-only').forEach((el) => { el.style.display = 'flex'; });
    if (SITE_MARKET === 'AU' || localisedPage) return;
    localisedPage = true;
    if (NO_REWORDING.test(window.location.pathname)) return;
    document.documentElement.lang = 'en-AU';
    const metric = isMetric();
    const localise = (t) => (metric ? metricText(applyAuWording(t)) : applyAuWording(t));
    document.title = localise(document.title);
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (node.parentElement?.closest('script, style, noscript, textarea')
            ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT)
    });
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = localise(node.nodeValue);
        if (text !== node.nodeValue) node.nodeValue = text;
    }
    for (const attr of ['alt', 'title', 'placeholder', 'aria-label']) {
        document.querySelectorAll(`[${attr}]`).forEach((el) => {
            const text = localise(el.getAttribute(attr));
            if (text !== el.getAttribute(attr)) el.setAttribute(attr, text);
        });
    }
}

// The site's market, or an account remembered on this device, applies as soon
// as the page loads.
if (account) setUnitSystem(account.units);
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyMarketToPage);
    document.addEventListener('DOMContentLoaded', setUpSiteSwitch);
} else {
    applyMarketToPage();
    setUpSiteSwitch();
}
